import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { toFixedAmount } from "../toFixedAmount";
import { Box, Divider, Typography } from "@mui/material";
import { PaymentBlockWithChip } from "./PaymentBlockWithChip";

function OrderPaymentInfo() {
  const { t } = useTranslation();
  const { order, loading } = useOutletContext();

  const cod = order.cod ? order.estimatedCost : "0.00";

  const additionalServicesPayments = useMemo(() => {
    let bySender = 0;
    let byRecipient = 0;

    order.services?.forEach((service) => {
      if (service.payment_by_sender) {
        bySender += parseFloat(service.amount);
      } else {
        byRecipient += parseFloat(service.amount);
      }
    });

    return {
      bySender,
      byRecipient,
    };
  }, [order.services]);

  const calculateTotalPayments = () => {
    const deliveryAndInsurance = parseFloat(order.deliveryCost) + parseFloat(order.insurance);
    let totalPayByRecipient = parseFloat(cod);
    let totalPayBySender = 0;

    if (order.codCommissionByRecipient) {
      totalPayByRecipient += parseFloat(order.codCommission);
    } else {
      totalPayBySender += parseFloat(order.codCommission);
    }

    if (order.paymentByRecipient) {
      totalPayByRecipient += deliveryAndInsurance;
    } else {
      totalPayBySender += deliveryAndInsurance;
    }

    totalPayByRecipient += additionalServicesPayments.byRecipient;
    totalPayBySender += additionalServicesPayments.bySender;

    totalPayByRecipient = toFixedAmount(totalPayByRecipient);
    totalPayBySender = toFixedAmount(totalPayBySender);

    return { totalPayByRecipient, totalPayBySender };
  };

  const { totalPayByRecipient, totalPayBySender } = calculateTotalPayments();

  return (
    <>
      <Box className={"flex items-center justify-start mx-4"}>
        <Typography className={"p-2 font-bold smMax:pb-0"}>
          {t("orderTracking.paymentPart")}
        </Typography>
      </Box>
      <Divider orientation={"horizontal"} flexItem className={"smMax:hidden"} />
      <Box className={"flex flex-col mx-4 p-1.5"}>
        <Box className={"flex items-center justify-stretch w-full smMax:flex-wrap"}>
          <Box className={"flex items-center w-full flex-wrap py-2 sm:w-1/2"}>
            <Typography variant={"subtitle1"} className={"text-gray-500 py-2"}>
              {t("orderTracking.paidByRecipient")}:
            </Typography>
            <Typography className={"ps-2 pe-1 font-bold"}>
              {totalPayByRecipient} {t("orderTracking.gel")}
            </Typography>
            {Boolean(additionalServicesPayments.byRecipient) && (
              <Typography className={"text-gray-500 text-sm"}>
                (
                {t("orderTracking.additionalServices.paymentIncludesGel", {
                  amount: toFixedAmount(additionalServicesPayments.byRecipient),
                })}
                )
              </Typography>
            )}
          </Box>
          <Box className={"flex items-center w-full flex-wrap py-2 sm:w-1/2"}>
            <Typography variant={"subtitle1"} className={"text-gray-500 py-2"}>
              {t("orderTracking.paidBySender")}:
            </Typography>
            <Typography className={"ps-2 pe-1 font-bold"}>
              {totalPayBySender} {t("orderTracking.gel")}
            </Typography>
            {Boolean(additionalServicesPayments.bySender) && (
              <Typography className={"text-gray-500 text-sm"}>
                (
                {t("orderTracking.additionalServices.paymentIncludesGel", {
                  amount: toFixedAmount(additionalServicesPayments.bySender),
                })}
                )
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          className={
            "flex items-center justify-stretch w-full smMax:flex-col md:flex-col xl:flex-row smMax:items-start"
          }
        >
          <PaymentBlockWithChip
            title={t("orderTracking.deliveryCost")}
            label={
              order.paymentByRecipient
                ? t("orderCreation.payRecipient")
                : t("orderCreation.paySender")
            }
            type={order.deliveryCost}
          />
          <PaymentBlockWithChip
            title={t("orderTracking.insurance")}
            label={
              order.paymentByRecipient
                ? t("orderCreation.payRecipient")
                : t("orderCreation.paySender")
            }
            type={order.insurance}
          />
        </Box>
        <Box
          className={
            "flex items-center justify-stretch w-full smMax:flex-col md:flex-col xl:flex-row smMax:items-start md:items-start"
          }
        >
          <PaymentBlockWithChip
            title={t("ordersTableHeaders.cod")}
            label={t("orderCreation.payRecipient")}
            type={cod}
          />
          <PaymentBlockWithChip
            title={t("orderTracking.codCommission")}
            label={
              order.codCommissionByRecipient
                ? t("orderCreation.payRecipient")
                : t("orderCreation.paySender")
            }
            type={order.codCommission}
          />
        </Box>
      </Box>
    </>
  );
}

export default OrderPaymentInfo;
